import { Controller } from '@hotwired/stimulus'

// synchronize selected inputs values (by `selector` in `selScope`)
// with the target link's searchParams or form inputs (node attached with `sync` method)

// customized at the controller level: values
// - selScope: selector scope, defaults to `this.element`
// - selector: css target nodes with `value` method, ex. form input
// or `sync` method level: params
// - selScope: further restrict `selector` search path
// - selector: override controller selectorValue (multiple buttons with `sync` attached)
// - paramName: override selected input param name, use for Array params ex. ids[];
//   multiple values separated with ',', export. 'ids[],q[id_in][]'

// used to:
// - choose authority record in edit record subfields form
// - create BR subfields report on BR list page
// - pass CR ids from doc_items list page to CR list page
// - pass chosen ids in pagination links
// - pass chosen ids to Report form
// - pass chosen ids in multiactions to create dict_value_change
// - pass chosen ids in multiactions on list pages (BR, CR, orders, wbc_records)
// - remove from, add to bundle (ex. BundledCopyRecord)
// - add subfield in MARC form (multiple subfield selects, one form)

export default class extends Controller {
  static values = {
    selScope: String, // set to override `this.element`
    selector: String, // can be set here or as param to `sync` method
  }

  connect () {
    // this.element, or arbitrary selector passed to `selScopeValue', further restricted by `selScope` passed as `sync` method param
    this.selScope = this.hasSelScopeValue
    ? document.querySelector(this.selScopeValue)
    : this.element
  }

  sync (e) {
    const { selScope, selector, paramName } = this._parseParams(e)
    // console.log(selector, selScope)
    const target = e.target?.form || e.target
    const handler = this._create_handler(target)

    this._clearExistingValues(target, paramName)

    selScope.forEach((scopeEl) => {
      scopeEl.querySelectorAll(selector).forEach((el) => {
        const name = paramName || el.name
        this._splitParamName(name).forEach(name => {
          handler(name, el.value)
        })
      })
    })
    // e.preventDefault()
  }

  // parse `selector` and `selScope` params
  _parseParams(e) {
    return {
      // opt, defaults to controller element or document
      // scope can return multiple elements → selector is run for each scope
      selScope: e.params.scope
        ? document.querySelectorAll(e.params.scope)
        : [this.selScope],
      // opt, override `selectorValue`
      selector: e.params.selector || this.selectorValue,
      // opt, override selected input name, use for array ex. 'ids[]'
      paramName: e.params.paramName
    }
  }

  // for links attaches search params
  // for forms appends hidden inputsh
  _create_handler(node) {
    const [link, form] = [this._getLink(node), this._getForm(node)]
    return form
    ? (name, value) => {
      const newInput = document.createElement('input')
      Object.assign(newInput, {
        type: 'hidden',
        name: name,
        value: value,
        autocomplete: 'off'
      })
      form.append(newInput)
    }
    : link
    ? (name, value) => {
      const newURL = new URL(link.href);
      newURL.searchParams.append(name, value)
      link.href = newURL
    }
    : console.error("unsupported element", node.nodeName)
  }

  _clearExistingValues(node, paramName) {
    if (!paramName) return
    const [link, form] = [this._getLink(node), this._getForm(node)]
    this._splitParamName(paramName).forEach(name => {
      if (form) {
        form.querySelectorAll(`[name='${name}']`)
          .forEach(el => el.remove())
      } else if (link) {
        const newURL= new URL(node.href)
        newURL.searchParams.delete(name)
        node.href = newURL
      }
    })
  }

  _getLink(node) {
    return node.nodeName == 'A' ? node : undefined
  }

  _getForm(node) {
    return node.nodeName == 'FORM'
      ? node
      : node['form'] // for example button
      ? node['form']
      : undefined
  }

  _splitParamName(s) {
    return s.split(',')
  }
}
